import { useCallback, useState } from 'react';
import { ApiRefusal } from '@gatecheckpro/api-client';
import type {
  SeatingConfigurationDocument,
  SeatingConfigurationDocumentView,
  SeatingConfigurationView,
} from '@gatecheckpro/contracts';
import { api } from '../api.ts';

/**
 * The seating configuration a person is editing, and how honest it is about
 * where it lives.
 *
 * Deliberately the same five states as the venue-map draft, because they are
 * the same five facts and an organizer moving between the two surfaces should
 * not have to learn a second vocabulary. What differs is the stake: a lost
 * topology edit costs somebody an afternoon of drawing, and a silently
 * overwritten seating configuration sells the wrong chairs on the night.
 *
 * Nothing merges. Two organizers who each set a stand's price cannot have their
 * work combined into a room neither of them configured.
 */
export type SeatingDraftStatus = 'persisted' | 'unsaved' | 'saving' | 'failed' | 'stale';

export interface SeatingDraft {
  readonly document: SeatingConfigurationDocument;
  readonly status: SeatingDraftStatus;
  readonly expectedDigest: string;
  readonly error: unknown;
  /** What the server holds, once a save has been refused as stale. */
  readonly serverDocument: SeatingConfigurationDocumentView | null;
  edit: (change: (current: SeatingConfigurationDocument) => SeatingConfigurationDocument) => void;
  save: () => Promise<boolean>;
  discardAndReload: () => Promise<void>;
}

/**
 * The document to edit, from the server's authoritative draft read.
 *
 * Every commercial term comes from here and nothing is reconstructed from the
 * venue map. That is not a preference: the map knows where a chair is, and
 * knows nothing about what it is sold as. Rebuilding a document from topology
 * would silently drop the product, the frozen version, the allocation and the
 * reason from every chair the organizer did not happen to touch.
 *
 * Only the *choice* travels back on save. The read carries both the product an
 * organizer picked and the version the server froze from it; the document
 * carries the product alone, because the frozen version is the server's answer
 * and echoing it would invite a client to propose one.
 */
export function toConfigurationDocument(
  document: SeatingConfigurationDocumentView,
): SeatingConfigurationDocument {
  return {
    venueMapVersionId: document.venueMapVersionId,
    seats: document.seats.map((seat) => ({
      venueMapSeatId: seat.venueMapSeatId,
      saleDisposition: seat.saleDisposition,
      ticketProductId: seat.ticketProductId,
      inventoryAllocationId: seat.inventoryAllocationId,
      reason: seat.reason,
    })),
    tables: document.tables.map((table) => ({
      venueMapTableId: table.venueMapTableId,
      saleMode: table.saleMode,
      ticketProductId: table.ticketProductId,
      inventoryAllocationId: table.inventoryAllocationId,
    })),
  };
}

export function useSeatingDraft(
  base: SeatingConfigurationDocumentView,
  versionPath: string,
): SeatingDraft {
  const [document, setDocument] = useState(() => toConfigurationDocument(base));
  const [expectedDigest, setExpectedDigest] = useState(base.configurationDigest);
  const [status, setStatus] = useState<SeatingDraftStatus>('persisted');
  const [error, setError] = useState<unknown>(null);
  const [serverDocument, setServerDocument] = useState<SeatingConfigurationDocumentView | null>(
    null,
  );

  const edit = useCallback(
    (change: (current: SeatingConfigurationDocument) => SeatingConfigurationDocument) => {
      setDocument((current) => change(current));
      // Editing over a stale refusal does not resolve it. The conflict is still
      // there, and the next save must still refuse rather than overwrite.
      setStatus((current) => (current === 'stale' ? 'stale' : 'unsaved'));
    },
    [],
  );

  const save = useCallback(async (): Promise<boolean> => {
    setStatus('saving');
    setError(null);
    try {
      // The write answers with the lean view, not the document. That is the
      // right answer to give: the document is the authoring transfer, and
      // sending 27 MiB back to confirm the 27 MiB just sent would double the
      // cost of every save. What the editor needs from a successful write is
      // the new digest to save against next.
      const saved = await api.request<SeatingConfigurationView>(`${versionPath}/configuration`, {
        method: 'PUT',
        body: { configuration: document },
        // The digest is a precondition on the write, not a field of the
        // configuration — so it travels as `If-Match`, where the server reads it.
        ifMatch: expectedDigest,
      });
      // The document stays as sent. The server accepted it whole, so the
      // browser and the server now hold the same commercial terms; re-reading
      // to learn that would only be a slower way to be told the same thing.
      setExpectedDigest(saved.version.configurationDigest);
      setServerDocument(null);
      setStatus('persisted');
      return true;
    } catch (caught) {
      setError(caught);
      // The typed code, not the transport status. 412 remains what If-Match
      // failing looks like on the wire, but a status is a category and this is
      // a specific event — branching on the status would eventually treat some
      // other precondition failure as "another organizer saved first" and offer
      // a reconcile for something that was never a conflict.
      const stale = caught instanceof ApiRefusal && caught.code === 'SEATING_CONFIGURATION_STALE';
      if (stale) {
        try {
          setServerDocument(
            await api.request<SeatingConfigurationDocumentView>(`${versionPath}/document`),
          );
        } catch {
          setServerDocument(null);
        }
      }
      setStatus(stale ? 'stale' : 'failed');
      return false;
    }
  }, [document, expectedDigest, versionPath]);

  const discardAndReload = useCallback(async () => {
    const fresh = await api.request<SeatingConfigurationDocumentView>(`${versionPath}/document`);
    setDocument(toConfigurationDocument(fresh));
    setExpectedDigest(fresh.configurationDigest);
    setServerDocument(null);
    setError(null);
    setStatus('persisted');
  }, [versionPath]);

  return { document, status, expectedDigest, error, serverDocument, edit, save, discardAndReload };
}

// --- Editing the document ----------------------------------------------------

/**
 * Applies one decision to many chairs.
 *
 * Bulk authoring resolves to concrete per-seat offers *here*, before anything
 * is saved. There is no section rule, no group rule and no inheritance, because
 * a precedence order between three of those is a question nobody can answer
 * from looking at a room — and the server requires exactly one resolved mapping
 * per sellable chair at activation anyway.
 */
export function applyToSeats(
  document: SeatingConfigurationDocument,
  seatIds: readonly string[],
  change: Omit<SeatingConfigurationDocument['seats'][number], 'venueMapSeatId'>,
): SeatingConfigurationDocument {
  // An upsert, not a map. A configuration starts with no offers at all — the
  // room exists, and nothing in it has been given a commercial meaning yet — so
  // an update-only version silently did nothing on a fresh night and every
  // bulk action appeared to work while changing not one thing.
  const targeted = new Set(seatIds);
  const updated = document.seats.map((seat) =>
    targeted.has(seat.venueMapSeatId) ? { ...seat, ...change } : seat,
  );
  const known = new Set(document.seats.map((seat) => seat.venueMapSeatId));
  const added = seatIds
    .filter((seatId) => !known.has(seatId))
    .map((seatId) => ({ venueMapSeatId: seatId, ...change }));

  return { ...document, seats: [...updated, ...added] };
}

/**
 * Sets a table's sale mode, and clears what the other mode had configured.
 *
 * Switching mode is not a cosmetic change: a whole-table sale and per-seat
 * sales are different commercial objects, and leaving the previous mode's
 * mapping behind is how a configuration reaches activation carrying both. The
 * server refuses that; doing it here means the organizer sees it happen rather
 * than meeting a refusal minutes later.
 */
export function setTableSaleMode(
  document: SeatingConfigurationDocument,
  venueMapTableId: string,
  saleMode: SeatingConfigurationDocument['tables'][number]['saleMode'],
  seatIdsOnTable: readonly string[],
  /**
   * What a whole table is sold as. Required for `WHOLE_TABLE` and forbidden for
   * `INDIVIDUAL_SEATS`, because in the first the table is the thing being sold
   * and in the second it is furniture the chairs happen to stand around.
   */
  sale: { ticketProductId: string | null; inventoryAllocationId: string | null } = {
    ticketProductId: null,
    inventoryAllocationId: null,
  },
): SeatingConfigurationDocument {
  const onTable = new Set(seatIdsOnTable);
  // Upsert, for the same reason the seat version does: a configuration starts
  // with no table offers at all, so an update-only version could never set a
  // mode on a table the organizer had not somehow already configured.
  const known = document.tables.some((table) => table.venueMapTableId === venueMapTableId);
  const tables = known
    ? document.tables
    : [
        ...document.tables,
        {
          venueMapTableId,
          saleMode,
          ticketProductId: saleMode === 'WHOLE_TABLE' ? sale.ticketProductId : null,
          inventoryAllocationId: saleMode === 'WHOLE_TABLE' ? sale.inventoryAllocationId : null,
        },
      ];

  return {
    ...document,
    tables: tables.map((table) =>
      table.venueMapTableId === venueMapTableId
        ? {
            ...table,
            saleMode,
            // A whole-table sale carries its own mapping; per-seat mode has none.
            ticketProductId:
              saleMode === 'WHOLE_TABLE' ? (sale.ticketProductId ?? table.ticketProductId) : null,
            inventoryAllocationId:
              saleMode === 'WHOLE_TABLE'
                ? (sale.inventoryAllocationId ?? table.inventoryAllocationId)
                : null,
          }
        : table,
    ),
    /*
     * A chair at a table sold whole is not individually on sale.
     *
     * Stripping its product while leaving it `SELLABLE` builds a document the
     * server refuses twice over — a seat on sale with nothing to draw on, and
     * a seat on sale at a table that carries the terms. That was invisible
     * while a new draft started empty, because there was no offer on those
     * chairs to strip. A draft cloned from a selling room has one.
     *
     * An existing reason is kept: those are somebody's words about that chair,
     * and a table mode is not the place to overwrite them.
     */
    seats: document.seats.map((seat) => {
      if (!onTable.has(seat.venueMapSeatId) || saleMode !== 'WHOLE_TABLE') return seat;
      return {
        ...seat,
        saleDisposition: seat.saleDisposition === 'SELLABLE' ? 'EXCLUDED' : seat.saleDisposition,
        ticketProductId: null,
        inventoryAllocationId: null,
        reason: seat.reason ?? 'Sold with the table',
      };
    }),
  };
}
